import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  Spinner,
  Text,
  VStack,
  useBreakpointValue,
} from "@chakra-ui/react";
import { format } from "date-fns";
import { ArrowRight, CheckCircle2, FileSignature } from "lucide-react";
import React, { memo, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  MFAttestationSummary,
  MF_ACCESS_REVIEWS,
} from "state/members/accessReviews";
import PageHeader from "../PageHeader";
import { signOffCtaLabel } from "./signOff";
import StatusBadge from "./StatusBadge";

const AttestationCard = memo(
  ({ attestation }: { attestation: MFAttestationSummary }) => {
    const navigate = useNavigate();
    const isMobile = useBreakpointValue({ base: true, md: false });

    const handleNavigate = () => {
      navigate(`/members/attestations/${attestation.id}`);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleNavigate();
      }
    };

    const cycleLabel = attestation.cycleName || "Access review";
    const resourceLabel = attestation.resourceName || "All resources";
    const dateLabel = format(new Date(attestation.insertedAt), "do MMMM yyyy");

    return (
      <Box
        // Chakra's `_groupHover` requires a parent with role="group". We keep
        // that role and add an explicit aria-label so screen readers announce
        // the card as a navigation target rather than a generic group.
        role="group"
        as="article"
        tabIndex={0}
        onClick={handleNavigate}
        onKeyDown={handleKeyDown}
        aria-label={`Open ${cycleLabel} attestation for ${resourceLabel}`}
        cursor="pointer"
        w="full"
        bg="bg.container"
        border="1px solid"
        borderColor="border.default"
        borderRadius="16px"
        // Card already exceeds 44px on every breakpoint; explicit minH
        // documents the contract for future edits.
        minH="64px"
        p={{ base: 4, md: 6 }}
        transitionProperty="border-color, box-shadow, transform"
        transitionDuration="150ms"
        transitionTimingFunction="ease-out"
        _hover={{
          borderColor: "border.hover",
          boxShadow:
            "0px 0px 1px 0px rgba(0,0,0,0.32), 0px 1px 4px 0px rgba(0,0,0,0.08), 0px 2px 6px 0px rgba(0,0,0,0.06)",
        }}
        _focusVisible={{
          outline: "none",
          borderColor: "border.focus",
          boxShadow: "0 0 0 3px var(--chakra-colors-p-100)",
        }}
        _active={{
          transform: "scale(0.996)",
        }}
      >
        <Flex
          direction={{ base: "column", md: "row" }}
          align={{ base: "stretch", md: "center" }}
          justify="space-between"
          gap={4}
          w="full"
        >
          <HStack spacing={4} align="flex-start" flex="1" minW={0}>
            <Flex
              align="center"
              justify="center"
              flexShrink={0}
              w="40px"
              h="40px"
              borderRadius="10px"
              bg="p.50"
              border="1px solid"
              borderColor="p.100"
            >
              <Icon as={FileSignature} boxSize="18px" color="p.600" />
            </Flex>
            <VStack align="flex-start" spacing="6px" flex="1" minW={0}>
              <Text
                fontFamily="HubotSans, sans-serif"
                fontSize={{ base: "16px", md: "18px" }}
                fontWeight="500"
                color="text.body.primary"
                lineHeight="1.3"
                noOfLines={2}
              >
                {cycleLabel}
              </Text>
              <HStack
                spacing={2}
                align="center"
                flexWrap="wrap"
                rowGap="4px"
              >
                <Text
                  fontSize="13px"
                  fontWeight="500"
                  color="text.body.secondary"
                  noOfLines={1}
                >
                  {resourceLabel}
                </Text>
                <Text fontSize="13px" color="text.body.tertiary">
                  •
                </Text>
                <Text fontSize="13px" color="text.body.tertiary">
                  Issued {dateLabel}
                </Text>
              </HStack>
            </VStack>
          </HStack>

          <HStack
            spacing={3}
            justify={{ base: "space-between", md: "flex-end" }}
            flexShrink={0}
          >
            <StatusBadge status={attestation.status} />
            <Button
              // The whole card is the focusable target — this CTA is visual
              // only. tabIndex=-1 + aria-hidden prevents double-tab traversal
              // and double-announcement to screen readers.
              size={isMobile ? "sm" : "md"}
              variant="primary"
              minH="44px"
              aria-hidden="true"
              tabIndex={-1}
              rightIcon={
                <Icon
                  as={ArrowRight}
                  boxSize="14px"
                  transitionProperty="transform"
                  transitionDuration="150ms"
                  transitionTimingFunction="ease-out"
                  _groupHover={{ transform: "translateX(2px)" }}
                />
              }
              onClick={(e) => {
                e.stopPropagation();
                handleNavigate();
              }}
            >
              {signOffCtaLabel(attestation)}
            </Button>
          </HStack>
        </Flex>
      </Box>
    );
  },
);
AttestationCard.displayName = "AttestationCard";

const EmptyState = memo(() => (
  <Box
    // role="status" + aria-live="polite" so screen readers announce "All caught up"
    // when the list resolves to empty.
    role="status"
    aria-live="polite"
    w="full"
    bg="bg.container"
    border="1px dashed"
    borderColor="border.default"
    borderRadius="16px"
    px={{ base: 6, md: 10 }}
    py={{ base: 10, md: 14 }}
  >
    <VStack spacing={4} textAlign="center">
      <Flex
        align="center"
        justify="center"
        w="56px"
        h="56px"
        borderRadius="full"
        bg="success.50"
        border="1px solid"
        borderColor="success.100"
      >
        <Icon as={CheckCircle2} boxSize="24px" color="success.600" aria-hidden="true" />
      </Flex>
      <VStack spacing={2} maxW="420px">
        <Text
          fontFamily="HubotSans, sans-serif"
          fontSize="18px"
          fontWeight="500"
          color="text.body.primary"
          lineHeight="1.3"
          sx={{ textWrap: "balance" }}
        >
          All caught up
        </Text>
        <Text
          fontSize="14px"
          fontWeight="400"
          color="text.body.tertiary"
          lineHeight="1.5"
          sx={{ textWrap: "pretty" }}
        >
          Pending attestations will appear here when a review you need to
          attest to is ready for your sign-off.
        </Text>
      </VStack>
    </VStack>
  </Box>
));
EmptyState.displayName = "EmptyState";

const LoadingState = memo(() => (
  <Flex w="full" justify="center" py={10}>
    <Spinner size="md" color="text.body.tertiary" />
  </Flex>
));
LoadingState.displayName = "LoadingState";

const Attestations: React.FC = () => {
  const { data, isLoading } = MF_ACCESS_REVIEWS.useListMyAttestations();

  // Surface only pending attestations on this page (per spec).
  const pending = useMemo(
    () => data.filter((a) => a.status === "pending"),
    [data],
  );

  return (
    <VStack
      w="full"
      align="flex-start"
      spacing={0}
      pb={{ base: "120px", md: "160px" }}
    >
      <PageHeader
        title="Attestations"
        subtitle="Attest to the access reviews you've been asked to formally confirm."
      />
      <Divider mt="24px" mb="24px" borderColor="border.default" />

      {isLoading ? (
        <LoadingState />
      ) : pending.length === 0 ? (
        <EmptyState />
      ) : (
        <VStack w="full" align="stretch" spacing={3}>
          {pending.map((attestation) => (
            <AttestationCard
              key={attestation.id}
              attestation={attestation}
            />
          ))}
        </VStack>
      )}
    </VStack>
  );
};

export default Attestations;
